import React from 'react';
import { Select, Space, Typography } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { productionLanguages, getLanguage } from '@/i18n/languageRegistry';

const { Text } = Typography;

type Props = {
  compact?: boolean;
  showLabel?: boolean;
  style?: React.CSSProperties;
  /** Form.Item injects these when used as a form control */
  value?: string;
  onChange?: (code: string) => void;
};

/**
 * Global language switcher — production-supported UI languages only.
 */
export const LanguageSwitcher: React.FC<Props> = ({
  compact = false,
  showLabel = false,
  style,
  value,
  onChange,
}) => {
  const { t } = useTranslation('common');
  const { preferences, updatePreference } = useUserPreferences();
  const current =
    value ?? getLanguage(preferences.ui_language || preferences.language).code;
  const options = productionLanguages().map((l) => ({
    value: l.code,
    label: l.nativeName,
  }));

  const handleChange = async (code: string) => {
    if (onChange) {
      onChange(code);
      return;
    }
    await updatePreference('ui_language', code);
  };

  const select = (
    <Select
      size={compact ? 'small' : 'middle'}
      value={current}
      options={options}
      onChange={handleChange}
      style={{ minWidth: compact ? 100 : 160, ...style }}
      suffixIcon={<GlobalOutlined />}
      aria-label={t('interfaceLanguage')}
      popupMatchSelectWidth={false}
    />
  );

  if (!showLabel) return select;

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Text type="secondary">{t('interfaceLanguage')}</Text>
      {select}
    </Space>
  );
};

export default LanguageSwitcher;
